import { ChangeEvent, useCallback, useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { Link, useNavigate } from "react-router-dom";
import CopyRight from "../../components/CopyRight";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import GoogleAuthButton from "../../components/GoogleAuthButton";
import { usePersistStore, useStore } from "../../store";
import { isEmail } from "validator";
import { UserService } from "../../services";
import { Email, Password, Phone, Button, Text } from "../../controls";
import CommonModal from "../../components/CommonModal";
import PasswordChecklist from "../../components/PasswordChecklist";
import { Utils } from "../../utils";
import {
  captureRedirectFromUrl,
  consumePostLoginRedirect,
} from "../../utils/postLoginRedirect";

function Register() {
  const { setIsLoading, setIsGoogleSignup } = useStore((s: any) => s);
  const {
    setUser,
    setWatchlist,
    setFollowing,
    setFollowers,
    setToken,
    setIsVerifiedUser,
    currentLocation,
  } = usePersistStore((s: any) => s);
  const [values, setValues] = useState({
    name: "",
    phone: "",
    email: "",
    password1: "",
    password2: "",
  });
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [modalShow, setModalShow] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();

  // Carry `?redirectTo=` from /register through email activation and
  // back to /login so the user lands on the originally-requested page.
  useEffect(() => {
    captureRedirectFromUrl();
  }, []);
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const hanldeSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      if (!values?.name)
        return setValidationMessage("Please provide your name.");
      if (!values?.phone)
        return setValidationMessage("Please provide your phone number.");
      if (!values?.email || !isEmail(values?.email))
        return setValidationMessage("Please provide a valid email address.");
      if (!values?.password1)
        return setValidationMessage("Please provide your password.");
      if (!Utils.checkPasswordStrength(values.password1).ok)
        return setValidationMessage(
          "Password must be at least 8 characters and include a letter, a number, and a special character."
        );
      if (!values?.password2)
        return setValidationMessage("Please repeat your password.");
      if (values?.password1 !== values?.password2)
        return setValidationMessage(
          "Password and repeat password must be same."
        );
      if (!acceptedTerms)
        return setValidationMessage(
          "Please accept the terms and conditions to continue."
        );

      setDisableButton(true);
      setIsLoading(true);
      let result = await UserService.register({
        name: values.name,
        phone: values.phone,
        email: values.email,
        password: values.password1,
        location: currentLocation,
      });
      if (result?.error) {
        setIsLoading(false);
        setValidationMessage(result?.error);
        setDisableButton(false);
        return;
      }

      setIsLoading(false);
      setModalShow(true);
    },
    [
      values,
      acceptedTerms,
      currentLocation,
      setIsLoading,
      setValidationMessage,
      setDisableButton,
    ]
  );

  const handleGoogleSignup = useCallback(
    async (response: any) => {
      setIsLoading(true);
      let result = await UserService.signupWithGoogle({
        credential: response?.credential,
        location: currentLocation,
      });
      if (result?.error) {
        setIsLoading(false);
        setValidationMessage(result?.error);
        return;
      }

      setUser(result?.data?.user);
      setWatchlist(result?.data?.user?.favourites || []);
      setFollowing(result?.data?.user?.following || []);
      setFollowers(result?.data?.user?.followers || []);
      setToken(result?.data?.token);
      setIsVerifiedUser(false);
      setIsLoading(false);
      // Only prompt to create a password for brand-new accounts. Existing
      // users who land here via "signup" are logged in instead — the backend
      // omits `signupWithGoogle` for them.
      setIsGoogleSignup(result?.data?.signupWithGoogle === true);
      navigate(consumePostLoginRedirect(), { replace: true });
    },
    [
      setUser,
      setWatchlist,
      setFollowing,
      setFollowers,
      setToken,
      setIsVerifiedUser,
      navigate,
      setIsLoading,
      currentLocation,
      setIsGoogleSignup,
    ]
  );

  return (
    <>
      <div className="container">
        <div className="row h-100vh">
          <div className="col-md-5 loginLeft position-relative">
            <div className="d-flex align-items-center h-100 w-100">
              <div className="w-100 mt-5">
                <div className="text-md-start text-center">
                  <Link to="/">
                    <Logo width={100} height={100} />
                  </Link>
                </div>
                <h1>Welcome to Boll-E&trade; account</h1>
                <p>Create your account to get full access</p>
                <div className="formMain">
                  <Form>
                    <Text
                      name="name"
                      value={values?.name}
                      onChange={handleInputChange}
                      label="Full name"
                      placeholder="Enter your full name"
                      icon="bi-person"
                    />
                    <Phone
                      name="phone"
                      value={values?.phone}
                      onChange={handleInputChange}
                    />
                    <Email
                      value={values?.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                    />
                    <Password
                      name="password1"
                      value={values?.password1}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                    />
                    <PasswordChecklist value={values?.password1} />
                    <Password
                      name="password2"
                      label="Repeat password"
                      value={values?.password2}
                      onChange={handleInputChange}
                      autoComplete="new-password"
                    />
                    <Form.Check
                      type="checkbox"
                      id="acceptTerms"
                      className="termsCheck mb-2"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      label={
                        <>
                          I agree to the{" "}
                          <Link
                            to="/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms and Conditions
                          </Link>
                        </>
                      }
                    />
                    {validationMessage && (
                      <div className="validation-message">
                        {validationMessage}
                      </div>
                    )}
                    <Button
                      title="Register"
                      onClick={hanldeSubmit}
                      disabled={disableButton || !acceptedTerms}
                    />
                  </Form>
                </div>
                <div className="lineThrough">
                  <span>Or register with</span>
                </div>
                <div className="d-flex justify-content-center mb-3">
                  <GoogleAuthButton
                    onSuccess={handleGoogleSignup}
                    onError={() =>
                      setValidationMessage("Failed to login with Google")
                    }
                    text="signup_with"
                  />
                </div>
                <div className="authThemeToggle mb-4 mb-md-5 pb-md-5">
                  <ThemeToggle />
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-center registerMain gap-2">
              <span>New member?</span>
              <div className="d-flex gap-2">
                <Link to="/login">Login here</Link>
              </div>
            </div>
          </div>
          <div className="col-md-1"></div>
          <div className="col-md-6 position-relative">
            <div className="d-md-flex align-items-center h-100 d-none">
              <img src="/assets/imgs/register.png" className="w-100" alt="" />
            </div>
            <CopyRight />
          </div>
        </div>
      </div>
      <CommonModal
        title="Success!"
        message="Your account is created successfully! Please check your mailbox to activate your account. You can sign in after your account is activated."
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          navigate("/login");
        }}
      />
    </>
  );
}

export default Register;
